import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./filters.css";

const PRICE_TICKS = ["any", 10, 20, 30, 40, 50]; // first tick represents "Any" price

const FiltersPanel = ({
  currentLocalization,
  loading,
  // package type controls (optional per page)
  showPackageType = false,
  packageType = "",
  onPackageType = () => {},
  // existing filter values
  selectData = "",
  selectDay = "",
  selectPrice = "",
  // handlers
  onDataFilter,
  onDayFilter,
  onPriceFilter,
  onClearAll,
}) => {
  const { t } = useTranslation();
  const [showPrice, setShowPrice] = useState(false);
  const [priceIndex, setPriceIndex] = useState(0); // Default to "Any" (index 0)
  const [isApplying, setIsApplying] = useState(false); // New state for Apply button loading
  // Local, staged selections for Apply button
  const [localPackageType, setLocalPackageType] = useState(packageType || "");
  const [localData, setLocalData] = useState(selectData ?? "");
  const [localDay, setLocalDay] = useState(selectDay ?? "");

  const dirClass = currentLocalization == "ar" ? "rtl" : "ltr";

  const tickIndexFromPrice = useMemo(() => {
    if (selectPrice === "" || selectPrice === null || selectPrice === undefined) return null;
    const idx = PRICE_TICKS.indexOf(Number(selectPrice));
    return idx >= 0 ? idx : null;
  }, [selectPrice]);

  useEffect(() => {
    setPriceIndex(tickIndexFromPrice);
  }, [tickIndexFromPrice]);

  // keep local state in sync with external resets (e.g., Clear filter)
  useEffect(() => {
    setLocalPackageType(packageType || "");
  }, [packageType]);
  useEffect(() => {
    setLocalData(selectData ?? "");
  }, [selectData]);
  useEffect(() => {
    setLocalDay(selectDay ?? "");
  }, [selectDay]);

  const handleSliderChange = (e) => {
    const idx = Number(e.target.value);
    setPriceIndex(idx);
  };
  const hasChanges = () => {
    const priceValue = priceIndex === 0 || priceIndex === null ? "" : PRICE_TICKS[priceIndex];
    return (
      (showPackageType && localPackageType !== packageType) ||
      localData !== selectData ||
      localDay !== selectDay ||
      priceValue !== selectPrice
    );
  };

  const applyAll = async () => {
    try {
      setIsApplying(true);
      // Apply package type first and wait for completion
      if (showPackageType && localPackageType !== packageType) {
        await onPackageType?.(localPackageType);
        // Wait a bit for the API call to complete and state to update
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Then apply other filters
      const priceValue = priceIndex === 0 || priceIndex === null ? "" : PRICE_TICKS[priceIndex];
      
      // Apply filters one by one
      if (localData !== selectData) {
        await onDataFilter?.(localData);
      }
      if (localDay !== selectDay) {
        await onDayFilter?.(localDay);
      }
      if (priceValue !== selectPrice) {
        await onPriceFilter?.(priceValue);
      }
      setIsApplying(false);
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const clearAll = () => {
    setPriceIndex(null);
    setShowPrice(false);
    setLocalPackageType("");
    setLocalData("");
    setLocalDay("");
    onClearAll?.();
  };

  return (
    <div className="filters-panel">
      <div className={`filters-panel__header ${dirClass === "rtl" ? "row-reverse" : ""}`}>
        <h5 className="filters-panel__title">{t("countryFilter.part5")}</h5>
        <button className="filters-panel__clear" onClick={clearAll} disabled={loading}>
          {t("countryFilter.part29")}
        </button>
      </div>

      <div className="filters-panel__section">
        <span className="filters-panel__label">{t("countryFilter.part17")}</span>
        <div className={`filters-panel__field ${dirClass === "rtl" ? "row-reverse" : ""}`}>
          <select
            className="filters-panel__select"
            value={String(localDay)}
            onChange={(e) => setLocalDay(e.target.value === "" ? "" : Number(e.target.value))}
            disabled={loading}
          >
            <option value="">{t("countryFilter.part7")}</option>
            <option value="7">{t("countryFilter.part18")}</option>
            <option value="14">{t("countryFilter.part19")}</option>
            <option value="30">{t("countryFilter.part20")}</option>
            <option value="60">{t("countryFilter.part21")}</option>
            <option value="90">{t("countryFilter.part22")}</option>
          </select>
        </div>
      </div>

      <div className="filters-panel__section">
        <span className="filters-panel__label">{t("countryFilter.part10")}</span>
        <div className={`filters-panel__field ${dirClass === "rtl" ? "row-reverse" : ""}`}>
          <select
            className="filters-panel__select"
            value={String(localData)}
            onChange={(e) => setLocalData(e.target.value === "" ? "" : Number(e.target.value))}
            disabled={loading}
          >
            <option value="">{t("countryFilter.part7")}</option>
            <option value="3">{t("countryFilter.part11")}</option>
            <option value="5">{t("countryFilter.part12")}</option>
            <option value="8">{t("countryFilter.part13")}</option>
            <option value="12">{t("countryFilter.part14")}</option>
            <option value="20">{t("countryFilter.part15")}</option>
            <option value="50">{t("countryFilter.part16")}</option>
            <option value="0">{t("home.part33")}</option>
          </select>
        </div>
      </div>

      {showPackageType && (
        <div className="filters-panel__section">
          <span className="filters-panel__label">{t("countryFilter.part6")}</span>
          <div className={`filters-panel__field ${dirClass === "rtl" ? "row-reverse" : ""}`}>
            <select
              className="filters-panel__select"
              value={localPackageType}
              onChange={(e) => setLocalPackageType(e.target.value)}
              disabled={loading}
            >
              <option value="">{t("countryFilter.part7")}</option>
              <option value="countryName">{t("countryFilter.part8")}</option>
              <option value="Global">{t("countryFilter.part9")}</option>
            </select>
          </div>
        </div>
      )}

      {/* divider removed per design */}

      <div className="filters-panel__section">
        {!showPrice && (
          <div className="filters-panel__showMoreContainer">
            <button 
              onClick={() => setShowPrice(true)}
              style={{ border: "none", background: "none", color: "#686868ff" }}
              disabled={loading}
            >
              {t("countryFilter.showMoreFilters")}
            </button>
          </div>
        )}

        {showPrice && (
          <div className="filters-panel__section">
            <div className={`filters-panel__priceToggle ${dirClass === "rtl" ? "row-reverse" : ""}`}>
              <span className="filters-panel__label">{t("countryFilter.part23")} ({t("countryFilter.UpTo")})</span>
            </div>
            <div className="filters-panel__price">
              <input
                type="range"
                min={0}
                max={5}
                step={1}
                value={priceIndex ?? 0}
                onChange={handleSliderChange}
                className="filters-panel__slider"
                disabled={loading}
                style={{
                  background: (() => {
                    const percent = ((priceIndex ?? 0) / 5) * 100;
                    return `linear-gradient(to right, #00b073 0%, #00b073 ${percent}%, #D9D9D9 ${percent}%, #D9D9D9 100%)`;
                  })(),
                }}
              />
              <div className="filters-panel__ticks">
                {["Any", "$10", "$20", "$30", "$40", "$50"].map((label, i) => (
                  <span key={i} className={`filters-panel__tick ${priceIndex === i ? "active" : ""}`}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="filters-panel__apply">
        <button className="filters-panel__applyBtn" onClick={applyAll} disabled={loading || isApplying || !hasChanges()}>
          {isApplying ? t("Applying...") : t("Apply Filter")}
        </button>
      </div>
    </div>
  );
};

export default FiltersPanel;


